import {
  booleanAttribute,
  Component,
  computed,
  DestroyRef,
  effect,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Signal,
  signal,
} from '@angular/core';
import {
  GalaxyAbstractFilterInput,
  GalaxyFilterValueInterface,
  InnerGalaxyFilterChipInjectionToken,
} from '../interface';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { combineLatest, Observable, shareReplay, Subscription, tap } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, startWith, switchMap, withLatestFrom } from 'rxjs/operators';
import { SEARCH_DEBOUNCE_MS } from '../constants';
import { MAT_SELECT_CONFIG, MatSelectModule } from '@angular/material/select';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { CountryCode } from 'libphonenumber-js';
import { CountryCodeService } from '@vendasta/galaxy/utility/country-codes';
import { CountryOption, countryOptionsFactoryProvider } from '@vendasta/galaxy/utility/country-codes';
import { PhoneFieldsInterface } from '@vendasta/crm';
import { DigitOnlyModule } from '@uiowa/digit-only';
import { stopChromeAutofill } from './stop-chrome-autofill';

export const DEFAULT_COUNTRY_CODE = 'US';

@Component({
  selector: 'glxy-phone-filter-input',
  templateUrl: './phone-filter-input.component.html',
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    MatInputModule,
    GalaxyFormFieldModule,
    MatAutocompleteModule,
    NgxMatSelectSearchModule,
    MatSelectModule,
    DigitOnlyModule,
  ],
  providers: [
    countryOptionsFactoryProvider,
    CountryCodeService,
    {
      provide: GalaxyAbstractFilterInput,
      useExisting: forwardRef(() => GalaxyPhoneFilterInputComponent),
    },
    {
      provide: MAT_SELECT_CONFIG,
      useValue: { overlayPanelClass: 'phone-filter-country-selector-overlay-pane' },
    },
  ],
  styleUrls: ['./phone-filter-input.component.scss'],
})
export class GalaxyPhoneFilterInputComponent implements GalaxyAbstractFilterInput, OnInit, OnDestroy {
  @Input() set fieldId(value: string) {
    this._fieldId.set(value);
  }
  @Input() set initialValue(value: GalaxyFilterValueInterface[]) {
    if (value?.length > 0 && value[0].phone) {
      const startingPhoneNumber = value[0].phone.nationalNumber || '';
      const startingCountry = value[0].phone.isoCountryCode || DEFAULT_COUNTRY_CODE;
      const startingExtension = value[0].phone.extension || '';
      this.phoneNumberControl.setValue(startingPhoneNumber);
      this.countryControl.setValue(startingCountry);
      this.countryCodeService.countryCodeUsed(startingCountry as CountryCode);
      this.extensionControl.setValue(startingExtension);
      const phoneVals = {
        isoCountryCode: startingCountry,
        nationalNumber: startingPhoneNumber,
        extension: startingExtension,
      } as PhoneFieldsInterface;
      this.phoneFieldVals.set(phoneVals);
    }
  }

  @Input({ transform: booleanAttribute }) set disabled(value: boolean) {
    if (value) {
      this.phoneFormGroup.disable();
    }
  }

  @Output() valueChanges = new EventEmitter<GalaxyFilterValueInterface[]>();

  searchCountryControl = new FormControl('');
  private countryControl = new FormControl('US');
  private phoneNumberControl = new FormControl('', { nonNullable: true });
  private extensionControl = new FormControl('');
  phoneFormGroup: FormGroup = new FormGroup({
    country: this.countryControl,
    phoneNumber: this.phoneNumberControl,
    extension: this.extensionControl,
  });

  disableAutoComplete: string = stopChromeAutofill();

  selectedCountry = computed(() => {
    return this.countryCodeService.getCountryOption(this.phoneFieldVals().isoCountryCode as CountryCode);
  });
  selectedCountryCallingCode = computed(() => this.selectedCountry()?.callingCode || '');
  countryOptions$: Observable<CountryOption[]> = this.countryCodeService.countryOptions$.pipe(
    distinctUntilChanged(),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );
  filteredCountryOptions$: Observable<CountryOption[]> = this.searchCountryControl.valueChanges.pipe(
    startWith(''),
    withLatestFrom(this.countryOptions$),
    map(([filterValue, countryOptions]) => {
      const fVal = filterValue || '';
      return countryOptions.filter((c) => {
        return (
          c.callingCode.includes(fVal) ||
          c.countryCode.toLowerCase().includes(fVal?.toLowerCase()) ||
          c.countryName.toLowerCase().includes(fVal?.toLowerCase())
        );
      });
    }),
  );

  private phoneFieldVals = signal<PhoneFieldsInterface>({
    isoCountryCode: 'US',
    nationalNumber: '',
    extension: '',
  } as PhoneFieldsInterface);

  searchTerm = toSignal(this.phoneFormGroup.valueChanges, { initialValue: '' });
  _fieldId = signal('');
  availableOptions = signal<string[]>([]);

  subscriptions: Subscription[] = [];
  filteredOptions: Signal<string[]> = computed(() => this.availableOptions());

  private readonly filtersDependencies = inject(InnerGalaxyFilterChipInjectionToken);
  constructor(
    private readonly countryCodeService: CountryCodeService,
    readonly destroyRef: DestroyRef,
  ) {
    effect(() => {
      const value = this.searchTerm();
      if (value === '') {
        this.valueChanges.emit([]);
        return;
      }
      this.valueChanges.emit([
        {
          phone: { isoCountryCode: value?.country, nationalNumber: value?.phoneNumber, extension: value?.extension },
        },
      ]);
    });

    const searchTerm$ = toObservable(this.searchTerm);
    const fieldId$ = toObservable(this._fieldId);

    this.subscriptions.push(
      combineLatest([searchTerm$, fieldId$])
        .pipe(
          filter(([, fieldId]) => !!fieldId),
          debounceTime(SEARCH_DEBOUNCE_MS),
          switchMap(([searchTerm, fieldId]) => {
            const stringSearch = this.bundlePhoneNumber(searchTerm);
            return this.filtersDependencies.getFieldOptions(fieldId, stringSearch);
          }),
          shareReplay(1),
        )
        .subscribe((options) => this.availableOptions.set(options)),
    );
  }

  ngOnInit(): void {
    this.phoneFormGroup.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((newVal) => {
          this.phoneFieldVals.set({
            isoCountryCode: newVal?.country,
            nationalNumber: newVal?.phoneNumber,
            extension: newVal?.extension,
          });
        }),
      )
      .subscribe();
  }

  bundlePhoneNumber(phoneValues: any | string): string {
    if (!phoneValues) {
      return phoneValues;
    }
    let bundle = '';
    if ('country' in phoneValues && phoneValues['country'].length > 0) {
      bundle += this.selectedCountry()?.callingCode;
    }
    if ('phoneNumber' in phoneValues && phoneValues['phoneNumber'].length > 0) {
      bundle += phoneValues['phoneNumber'];
      return bundle;
    }
    return '';
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  clearValue() {
    this.countryControl.setValue('US');
    this.phoneNumberControl.setValue('');
    this.extensionControl.setValue('');
  }
}
